/**
 * i18next logger that suppresses the false-positive noise emitted while the translation
 * bundle is still loading.
 *
 * Translations load asynchronously via http-backend, but many `*.constants.js` modules build
 * their option lists at import time (e.g. `export const industryOptions = getIndustryOptions()`),
 * calling i18n.t() during the initial module-graph evaluation — before the en bundle has landed.
 * In `debug` mode that produced a flood of `missingKey undefined ...` lines on every first load,
 * for keys that exist perfectly well; they just weren't loaded yet.
 *
 * The messages arrive on two different logger channels: i18next reports `missingKey` via
 * logger.log, while the unresolved-namespace and not-initialised notices come through
 * logger.warn. Both are gated here — a gate on either channel alone silently misses half of
 * them. Everything else passes through untouched, and once `isReady()` turns true all three
 * messages are reported again so genuinely missing keys still surface during normal use.
 */

// Matched as substrings: i18next prefixes args[0] (e.g. `i18next::translator: missingKey`).
export const PRE_LOAD_NOISE = [
  'missingKey',
  "won't get resolved as namespace",
  'hasLoadedNamespace: i18next was not initialized'
];

export const isPreLoadNoise = args => Array.isArray(args)
  && typeof args[0] === 'string'
  && PRE_LOAD_NOISE.some(msg => args[0].indexOf(msg) > -1);

/**
 * @param {() => boolean} isReady - true once the initial bundle has loaded.
 * @param {Console} [out] - console to write to; injectable for tests.
 */
export const createGatedLogger = (isReady, out = console) => {
  const gate = channel => (args) => {
    if (!isReady() && isPreLoadNoise(args)) return;
    out[channel](...args);
  };
  return {
    type: 'logger',
    log: gate('log'),
    warn: gate('warn'),
    // Errors are never gated: an error during load is real and must not be swallowed.
    error(args) { out.error(...args); }
  };
};

export default createGatedLogger;
